import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { toast } from 'sonner'

import {
  ACCESS_REASON_HEADER,
  ACCESS_REASON_REQUIRED_CODE,
  encodeAccessReasonHeader,
  requestAccessReason,
  useAccessReasonStore,
} from '../stores/accessReasonStore'
import { useAuthStore } from '../stores/authStore'
import { isAuthSuppressToastCode } from './accountsErrors'
import {
  loginPathWithReason,
  LoginReason,
  SESSION_AUTH_TOAST_STORAGE_KEY,
} from './auth-login-query'
import { performTokenRefresh, SESSION_CHANGED_CODE } from './auth-token-refresh'

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * The caller shows its own error message, so skip the generic 403/404/500/network toast
     * (session, account-removal and rate-limit handling still apply).
     */
    skipGlobalErrorToast?: boolean
  }
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

const ACCOUNT_REMOVAL_CODES = new Set([
  'ACCOUNT_REMOVED',
  'ACCOUNT_DELETED',
  'USER_REMOVED',
  'USER_DELETED',
  'ACCOUNT_DISABLED',
])
const PERMISSION_GUARD_CODES = new Set([
  'PERMISSION_DENIED',
  'SUPER_ADMIN_PROTECTED',
  'DELETE_REQUIRES_DISABLED',
  // Registration invite pages explain the wrong-account case inline.
  'INVITE_WRONG_ACCOUNT',
])

let isHandlingAuthFailure = false

/** Identical messages share a toast id, so a burst of failing requests shows one toast. */
const toastErrorOnce = (message: string) => toast.error(message, { id: `api-error:${message}` })

const isReadRequest = (error: AxiosError) => error.config?.method?.toLowerCase() === 'get'

type ForceLogoutOptions = {
  redirectTo?: string
  toastMessage?: string
}

const forceLogout = ({ redirectTo = '/login', toastMessage }: ForceLogoutOptions = {}) => {
  if (isHandlingAuthFailure) {
    return false
  }
  isHandlingAuthFailure = true

  const { clearAuth } = useAuthStore.getState()
  clearAuth()

  if (toastMessage) {
    try {
      sessionStorage.setItem(SESSION_AUTH_TOAST_STORAGE_KEY, toastMessage)
    } catch {
      // ignore quota / private mode
    }
    toast.error(toastMessage, { duration: 12_000 })
  }

  if (typeof window !== 'undefined') {
    window.location.replace(redirectTo)
  }

  return true
}

type ApiErrorResponse = {
  /** FERPA validation errors carry `error.fields` (Django `Model.clean()` messages). */
  error?: {
    code?: string | null
    message?: string | null
    fields?: Record<string, unknown> | null
  } | null
  code?: string | null
  message?: string | null
  errors?: Record<string, unknown> | null
  /** Nested creates return one plain message per payload path, e.g. `grade_levels[1].code` */
  field_errors?: Record<string, unknown> | null
  meta?: Record<string, unknown> | null
  /** Some endpoints return a single field name for validation errors */
  field?: string | null
  /** Ninja/Pydantic 422 body: a list of `{ loc, msg }` entries */
  detail?: unknown
}

/** Turns Ninja/Pydantic 422 `detail` entries into a message and a per-field error map. */
const parseValidationDetail = (detail: unknown) => {
  if (!Array.isArray(detail)) {
    return typeof detail === 'string' && detail ? { message: detail } : {}
  }
  const errors: Record<string, string[]> = {}
  let message: string | undefined
  for (const item of detail) {
    if (!item || typeof item !== 'object') continue
    const { loc, msg } = item as { loc?: unknown; msg?: unknown }
    if (typeof msg !== 'string' || !msg) continue
    const path = (Array.isArray(loc) ? loc : [])
      .filter(part => part !== 'body' && part !== 'payload' && part !== 'query')
      .join('.')
    const readable = msg.replace(/^Value error, /, '')
    message ??= readable
    if (path) errors[path] = [...(errors[path] ?? []), readable]
  }
  return { message, errors: Object.keys(errors).length ? errors : undefined }
}

const normalizeErrorMap = (errors: Record<string, unknown> | null | undefined) => {
  if (!errors || typeof errors !== 'object') {
    return undefined
  }

  const normalized: Record<string, string[]> = {}

  for (const [field, value] of Object.entries(errors)) {
    if (Array.isArray(value)) {
      const messages = value
        .map(item => (item == null ? null : String(item)))
        .filter((item): item is string => Boolean(item))
      if (messages.length) {
        normalized[field] = messages
      }
      continue
    }

    if (value != null) {
      normalized[field] = [String(value)]
    }
  }

  return Object.keys(normalized).length ? normalized : undefined
}

const extractErrorPayload = (error: AxiosError) => {
  const data = error.response?.data as ApiErrorResponse | undefined

  const detail = parseValidationDetail(data?.detail)
  const code = data?.error?.code ?? (typeof data?.code === 'string' ? data.code : undefined)
  const message =
    (typeof data?.error === 'string' ? (data.error as string) : undefined) ??
    (typeof data?.error?.message === 'string' ? data.error.message : undefined) ??
    (typeof data?.message === 'string' ? data.message : undefined) ??
    detail.message
  const fieldErrors = normalizeErrorMap(data?.field_errors ?? undefined)
  const listed = normalizeErrorMap(data?.errors ?? data?.error?.fields ?? undefined)
  let errors = (listed || fieldErrors ? { ...fieldErrors, ...listed } : undefined) ?? detail.errors
  const field = typeof data?.field === 'string' ? data.field : undefined
  if (field && message && (!errors || !errors[field])) {
    errors = { ...errors, [field]: [message] }
  }
  const meta = data?.meta && typeof data.meta === 'object' ? data.meta : undefined

  return { code, message, errors, meta, field, status: error.response?.status }
}

const getRetryAfterSeconds = (meta: Record<string, unknown> | undefined) => {
  const retryAfter = meta?.retry_after
  if (typeof retryAfter === 'number' && Number.isFinite(retryAfter) && retryAfter > 0) {
    return retryAfter
  }
  if (typeof retryAfter === 'string') {
    const parsed = Number(retryAfter)
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }
  }
  return undefined
}

const formatRetryAfter = (seconds: number) => {
  if (seconds < 60) {
    const roundedSeconds = Math.ceil(seconds)
    return `${roundedSeconds} second${roundedSeconds === 1 ? '' : 's'}`
  }

  const roundedMinutes = Math.ceil(seconds / 60)
  return `${roundedMinutes} minute${roundedMinutes === 1 ? '' : 's'}`
}

export interface ParsedApiError {
  code?: string
  message?: string
  errors?: Record<string, string[]>
  meta?: Record<string, unknown>
  /** Present when the API returns a top-level `field` for a single-field error */
  field?: string
  status?: number
  raw: unknown
}

export const parseApiError = (error: unknown): ParsedApiError => {
  if (axios.isAxiosError(error)) {
    const payload = extractErrorPayload(error)
    const fallbackMessage =
      payload.message ??
      (typeof error.response?.data === 'string' ? error.response?.data : undefined) ??
      error.message

    return {
      code: payload.code ?? undefined,
      message: fallbackMessage,
      errors: payload.errors,
      meta: payload.meta,
      field: payload.field,
      status: payload.status,
      raw: error,
    }
  }

  if (error instanceof Error) {
    return { message: error.message, raw: error }
  }

  return { message: 'An unexpected error occurred', raw: error }
}

const isAccountRemoval = (error: AxiosError) => {
  const { code, message } = extractErrorPayload(error)
  if (code && ACCOUNT_REMOVAL_CODES.has(code)) {
    return true
  }
  if (message && /account (?:has been )?(removed|deleted|disabled)/i.test(message)) {
    return true
  }
  return false
}

apiClient.interceptors.request.use(
  config => {
    const { accessToken } = useAuthStore.getState()
    if (accessToken) {
      config.headers.Authorization = 'Bearer ' + accessToken
    }

    const { reason } = useAccessReasonStore.getState()
    if (reason && !config.headers[ACCESS_REASON_HEADER]) {
      config.headers[ACCESS_REASON_HEADER] = encodeAccessReasonHeader(reason)
    }

    if (typeof document !== 'undefined') {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken
      }
    }

    return config
  },
  error => Promise.reject(error),
)

type AccessReasonRetryConfig = InternalAxiosRequestConfig & { _accessReasonRetry?: boolean }

/**
 * Super admin access (break-glass): the API refused a student-record read without a reason.
 * Ask once (concurrent failures share the dialog), then replay the request with the header.
 * A request is replayed at most once; cancelling rejects with the original 403.
 */
const retryWithAccessReason = async (error: AxiosError) => {
  const config = error.config as AccessReasonRetryConfig | undefined
  if (!config || config._accessReasonRetry) return Promise.reject(error)

  const sent = config.headers?.[ACCESS_REASON_HEADER]
  const current = useAccessReasonStore.getState().reason
  let reason: string
  if (current && encodeAccessReasonHeader(current) !== sent) {
    // Sent before the reason was given (or while it changed): replay with the current one.
    reason = current
  } else {
    try {
      reason = await requestAccessReason()
    } catch {
      toast.error('Student records stay closed until you give a reason for super admin access.', {
        id: 'access-reason-cancelled',
      })
      return Promise.reject(error)
    }
  }

  config._accessReasonRetry = true
  config.headers[ACCESS_REASON_HEADER] = encodeAccessReasonHeader(reason)
  return apiClient(config)
}

apiClient.interceptors.response.use(
  response => {
    return response
  },
  async (error: AxiosError) => {
    const { code, message, meta } = extractErrorPayload(error)

    if (isAccountRemoval(error)) {
      forceLogout({
        redirectTo: '/login?account=removed',
        toastMessage: message ?? 'Your account has been removed. Please contact support.',
      })
      return Promise.reject(error)
    }

    // Rate limits (e.g. `RATE_LIMITED` on accept-invitation) must show a global toast even when
    // the error `code` is listed in accounts suppress-toast codes.
    if (error.response?.status === 429) {
      const retryAfterSeconds = getRetryAfterSeconds(meta)
      if (retryAfterSeconds !== undefined) {
        toast.error(
          `Too many attempts. Please try again in ${formatRetryAfter(retryAfterSeconds)}.`,
        )
      } else if (message) {
        toast.error(message)
      } else {
        toast.error('Too many attempts. Please try again later.')
      }
      return Promise.reject(error)
    }

    if (error.response?.status === 403 && code === ACCESS_REASON_REQUIRED_CODE) {
      return retryWithAccessReason(error)
    }

    // Don't show generic toasts for auth-specific error codes - let components handle them
    if (code && isAuthSuppressToastCode(code)) {
      return Promise.reject(error)
    }

    const originalRequest = (error.config || {}) as AxiosRequestConfig & {
      _retry?: boolean
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        await performTokenRefresh()
        const newAccess = useAuthStore.getState().accessToken
        if (newAccess) {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: 'Bearer ' + newAccess,
          }
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        const refreshParsed = parseApiError(refreshError)
        if (refreshParsed.code === SESSION_CHANGED_CODE) {
          // The user signed out or switched accounts meanwhile; do not sign the new session out
          // or replay this request with its token.
          return Promise.reject(refreshError)
        }
        if (
          refreshParsed.code === 'TOKEN_NOT_VALID' ||
          refreshParsed.code === 'TOKEN_REFRESH_FAILED'
        ) {
          forceLogout({
            redirectTo: loginPathWithReason(LoginReason.sessionExpired),
            toastMessage: 'Your session expired. Please sign in again.',
          })
        } else {
          forceLogout({
            redirectTo: loginPathWithReason(LoginReason.sessionExpired),
            toastMessage: 'Session refresh failed. Please sign in again.',
          })
        }
        return Promise.reject(refreshError)
      }
    }

    if (error.response?.status === 401) {
      if (code === 'TOKEN_NOT_VALID') {
        forceLogout({
          redirectTo: loginPathWithReason(LoginReason.sessionExpired),
          toastMessage: 'Your session expired. Please sign in again.',
        })
      } else {
        forceLogout({
          redirectTo: loginPathWithReason(LoginReason.authRequired),
          toastMessage: 'Authentication required. Please sign in again.',
        })
      }
      return Promise.reject(error)
    }

    if (error.config?.skipGlobalErrorToast) {
      return Promise.reject(error)
    }

    // Don't force logout on 403 — school admins may get 403 for out-of-scope actions.
    // Reads (GET) are TanStack queries whose pages render their own error state; toasting each
    // one stacks identical "Access denied" messages. Mutations still toast.
    if (error.response?.status === 403) {
      if (isReadRequest(error) || (code && PERMISSION_GUARD_CODES.has(code))) {
        return Promise.reject(error)
      }
      toastErrorOnce(message || 'You do not have permission to perform this action.')
      return Promise.reject(error)
    }

    if (error.response?.status === 404 && !isReadRequest(error)) {
      toastErrorOnce('The requested resource was not found')
    }

    if (error.response?.status === 500) {
      toastErrorOnce('An unexpected error occurred. Please try again later.')
    }

    if (!error.response) {
      // Aborted/superseded requests (e.g. React Query cancel on unmount/refetch) — not user-facing failures.
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        return Promise.reject(error)
      }
      // GETs are usually TanStack Query reads with retry; transient blips (incl. browser "CORS" surfacing as
      // ERR_NETWORK) should not spam toasts — the query UI / refetch handles recovery.
      if (isReadRequest(error)) {
        return Promise.reject(error)
      }
      toastErrorOnce('Network error. Please check your connection.')
    }

    return Promise.reject(error)
  },
)

export const handleApiError = (error: unknown): string => {
  const parsed = parseApiError(error)
  if (parsed.message) {
    return parsed.message
  }

  if (parsed.errors) {
    for (const messages of Object.values(parsed.errors)) {
      if (Array.isArray(messages) && messages.length) {
        return messages[0]
      }
    }
  }

  return 'An unexpected error occurred'
}

export const createFormData = (data: Record<string, unknown>): FormData => {
  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (value instanceof File) {
        formData.append(key, value)
      } else if (value instanceof Date) {
        formData.append(key, value.toISOString())
      } else if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value))
      } else {
        formData.append(key, String(value))
      }
    }
  })

  return formData
}
